import AsyncStorage from '@react-native-async-storage/async-storage';

const TEMP_ONBOARDING_KEY = 'temp_onboarding_data';

export interface TempOnboardingData {
  name?: string;
  interests?: string[];
  gender?: 'feminino' | 'masculino' | 'nao_binario' | 'prefiro_nao_dizer';
  supportRecipient?: 'self' | 'friend' | 'family';
  sponsorshipPreference?: 'godmother' | 'godfather' | 'maybe_later' | 'dont_want';
  addictionTypes?: string[];
  addictionFrequency?: string;
  appGoal?: string;
  selectedClinicId?: number;
  clinicSelectionProtocol?: 'selection_v1';
  timestamp?: number;
}

export class TempOnboardingService {
  static async getDataStrict(): Promise<TempOnboardingData> {
    const raw = await AsyncStorage.getItem(TEMP_ONBOARDING_KEY);
    if (!raw) return {};
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      throw new Error('Invalid onboarding draft');
    }
    return data;
  }

  static async saveClinicSelection(selectedClinicId: number): Promise<void> {
    if (!Number.isSafeInteger(selectedClinicId) || selectedClinicId < 1) {
      throw new Error('CLINIC_SELECTION_REQUIRED');
    }
    // Unlike a read for display, a failed read must not overwrite other answers.
    const raw = await AsyncStorage.getItem(TEMP_ONBOARDING_KEY);
    const existing = raw ? JSON.parse(raw) : {};
    await AsyncStorage.setItem(TEMP_ONBOARDING_KEY, JSON.stringify({
      ...existing, selectedClinicId, clinicSelectionProtocol: 'selection_v1', timestamp: Date.now(),
    }));
  }

  static async saveName(name: string): Promise<void> {
    try {
      const existing = await this.getData();
      await AsyncStorage.setItem(
        TEMP_ONBOARDING_KEY,
        JSON.stringify({
          ...existing,
          name,
          timestamp: Date.now(),
        })
      );
      console.log('✅ Temporary name saved:', name);
    } catch (error) {
      console.error('❌ Error saving temporary name:', error);
      throw error;
    }
  }

  static async saveInterests(interests: string[]): Promise<void> {
    try {
      const existing = await this.getData();
      await AsyncStorage.setItem(
        TEMP_ONBOARDING_KEY,
        JSON.stringify({
          ...existing,
          interests,
          timestamp: Date.now(),
        })
      );
      console.log('✅ Temporary interests saved:', interests);
    } catch (error) {
      console.error('❌ Error saving temporary interests:', error);
      throw error;
    }
  }

  static async saveGender(gender: TempOnboardingData['gender']): Promise<void> {
    try {
      const existing = await this.getData();
      await AsyncStorage.setItem(
        TEMP_ONBOARDING_KEY,
        JSON.stringify({
          ...existing,
          gender,
          timestamp: Date.now(),
        })
      );
      console.log('✅ Temporary gender saved:', gender);
    } catch (error) {
      console.error('❌ Error saving temporary gender:', error);
      throw error;
    }
  }

  static async saveSupportRecipient(
    supportRecipient: TempOnboardingData['supportRecipient']
  ): Promise<void> {
    try {
      const existing = await this.getData();
      await AsyncStorage.setItem(
        TEMP_ONBOARDING_KEY,
        JSON.stringify({
          ...existing,
          supportRecipient,
          timestamp: Date.now(),
        })
      );
      console.log('✅ Temporary support recipient saved:', supportRecipient);
    } catch (error) {
      console.error('❌ Error saving temporary support recipient:', error);
      throw error;
    }
  }

  static async saveSponsorshipPreference(
    sponsorshipPreference: TempOnboardingData['sponsorshipPreference']
  ): Promise<void> {
    try {
      const existing = await this.getData();
      await AsyncStorage.setItem(
        TEMP_ONBOARDING_KEY,
        JSON.stringify({
          ...existing,
          sponsorshipPreference,
          timestamp: Date.now(),
        })
      );
      console.log('✅ Temporary sponsorship preference saved:', sponsorshipPreference);
    } catch (error) {
      console.error('❌ Error saving temporary sponsorship preference:', error);
      throw error;
    }
  }

  static async saveAddictionTypes(addictionTypes: string[]): Promise<void> {
    try {
      const existing = await this.getData();
      await AsyncStorage.setItem(
        TEMP_ONBOARDING_KEY,
        JSON.stringify({
          ...existing,
          addictionTypes,
          timestamp: Date.now(),
        })
      );
      console.log('✅ Temporary addiction types saved:', addictionTypes);
    } catch (error) {
      console.error('❌ Error saving temporary addiction types:', error);
      throw error;
    }
  }

  static async saveAddictionFrequency(addictionFrequency: string): Promise<void> {
    try {
      const existing = await this.getData();
      await AsyncStorage.setItem(
        TEMP_ONBOARDING_KEY,
        JSON.stringify({
          ...existing,
          addictionFrequency,
          timestamp: Date.now(),
        })
      );
      console.log('✅ Temporary addiction frequency saved:', addictionFrequency);
    } catch (error) {
      console.error('❌ Error saving temporary addiction frequency:', error);
      throw error;
    }
  }

  static async saveAppGoal(appGoal: string): Promise<void> {
    try {
      const existing = await this.getData();
      await AsyncStorage.setItem(
        TEMP_ONBOARDING_KEY,
        JSON.stringify({
          ...existing,
          appGoal,
          timestamp: Date.now(),
        })
      );
      console.log('✅ Temporary app goal saved:', appGoal);
    } catch (error) {
      console.error('❌ Error saving temporary app goal:', error);
      throw error;
    }
  }

  static async getData(): Promise<TempOnboardingData> {
    try {
      const data = await AsyncStorage.getItem(TEMP_ONBOARDING_KEY);
      if (!data) {
        return {};
      }
      return JSON.parse(data);
    } catch (error) {
      console.error('❌ Error retrieving temporary onboarding data:', error);
      return {};
    }
  }

  static async clearData(): Promise<void> {
    try {
      await AsyncStorage.removeItem(TEMP_ONBOARDING_KEY);
      console.log('✅ Temporary onboarding data cleared');
    } catch (error) {
      console.error('❌ Error clearing temporary onboarding data:', error);
    }
  }

  static async hasData(): Promise<boolean> {
    try {
      const data = await this.getData();
      return !!(data.name || data.interests || data.gender || data.supportRecipient || data.sponsorshipPreference || data.addictionTypes || data.addictionFrequency || data.appGoal);
    } catch (error) {
      return false;
    }
  }
}
